import axios from 'axios'
import localforage from 'localforage'
import promiseFinally from 'promise.prototype.finally'
import useragent from 'ua-parser-js'

import { PSApplication } from '../components/app/PSApplication'
import { CacheFactory } from '../models/CacheFactory'
import { SQLiteFactory } from '../models/SQLiteFactory'
import { InvalidDataError } from '../models/errors/InvalidDataError'
import { Result, SnakeCaseKeys, errorResult, okResult } from '../types/utils'
import { API_BASE, BOOK_BASE, REQUEST_TIMEOUT_SEC } from '../utils/constants'

promiseFinally.shim()

let browserId = localStorage.getItem('id')
if (!browserId) {
    // @ts-ignore allow msCrypto fallback
    const crypto = window.crypto || window.msCrypto

    const randomInt = crypto.getRandomValues(new Uint32Array(1))[0]
    browserId = randomInt.toString(16)
    localStorage.setItem('id', browserId)
}

const agent = useragent(navigator.userAgent)

export const apiClient = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    timeout: REQUEST_TIMEOUT_SEC * 1000,
    headers: {
        'X-Requested-With': 'DesktopPSRequest',
        'X-Requested-OS': `${agent.os.name} ${agent.os.version}`.substr(0, 19),
        'X-Requested-Build': '1000',
        'X-Requested-Device': browserId,
        'X-Requested-Make': 'desktop',
        'X-Requested-Type': `${agent.browser.name} ${agent.browser.version}`
            .substr(0, 49)
            .replace('.', '_'),
    },
})

export const generateCancelToken = () => axios.CancelToken.source()

export const apiClientSetup = () => {
    apiClient.interceptors.response.use((response) => {
        const loginRedirect: boolean =
            response.status === 302 && response.headers['Location'].startsWith('/login')
        const loginTimeout: boolean =
            response.status === 206 && response.data.code === 'LOGIN_TIMEOUT'
        const disabledLogin: boolean =
            response.status === 206 && response.data.code === 'ERR_USER_ACCOUNT_LOCKED_OUT'
        if (loginRedirect || loginTimeout || disabledLogin) {
            // If response is auth error, redirect user to /login
            // call logoutUser to be DRY
            localforage.clear()
            localStorage.clear()

            window.location.href = '/login'

            return new Promise(() => {})
        }
        else if (response.status === 206) {
            throw new InvalidDataError(response.data?.message || 'Invalid input data')
        }
        else if (response.status >= 400) {
            throw response
        }
        return response
    })
}

export const registerDevice = (token: any) => {
    const body: any = {
        address: token,
    }

    const savedToken = localStorage.getItem('savedToken')
    if (!PSApplication.device()) {
        console.error('No device found, cannot register')
        return
    }

    // If device address is the same, skip it
    if (savedToken === token) {
        return
    }

    body.ttl = PSApplication.device()?.ttl

    apiClient
        .post(`/device/${PSApplication.device().id}`, body)
        .then((resp) => resp.data)
        .then((json) => {
            localStorage.setItem('savedToken', json.device.address)
        })
        .catch(console.log)
}

export const loginWithPhoneNumber = async (phone: string, redirectUrl: string | null) => {
    const resp = await apiClient.post<{
        e164: string
        login_type: string
    }>('/login', { phone, redirectUrl })
    return {
        e164: resp.data.e164,
        loginType: resp.data.login_type,
    }
}

export type LoginResponse = {
    device: { device: string }
    login: { id: string; role: string; plan: string }
    industries?: any[]
    showIncome?: any
    requiresRole: boolean
}
export const defaultPathForLoginResponse = (loginResponse: LoginResponse) => {
    if (loginResponse.login.role === 'owner' || loginResponse.login.role === 'admin') {
        return '/'
    }
    else if (loginResponse.login.plan === 'franchise') {
        return '/reports'
    }
    else {
        return '/clients'
    }
}

export const loginWithCode = async (
    phone: string,
    code: string,
    redirectUrl: string | null
): Promise<Result<LoginResponse>> => {
    const resp = await apiClient.post<SnakeCaseKeys<LoginResponse>>('/login', {
        phone,
        code,
        redirectUrl,
    })
    if (!resp.data.login) {
        return errorResult(null)
    }
    return okResult<LoginResponse>({
        device: resp.data.device,
        login: resp.data.login,
        industries: resp.data.industries,
        showIncome: resp.data.show_income,
        requiresRole: resp.data.requires_role ?? false,
    })
}

export const loginWithAssociatedAccountUserID = async (
    associatedAccountUserID: string
): Promise<Result<LoginResponse>> => {
    const resp = await apiClient.post<SnakeCaseKeys<LoginResponse>>(
        `/login/${associatedAccountUserID}`
    )
    if (!resp.data.login) {
        return errorResult(null)
    }
    return okResult<LoginResponse>({
        device: resp.data.device,
        login: resp.data.login,
        industries: resp.data.industries,
        showIncome: resp.data.show_income,
        requiresRole: resp.data.requires_role ?? false,
    })
}

export const logout = async () => {
    try {
        await apiClient.post('/logout', { device: PSApplication.device()?.id })
        await SQLiteFactory.getInstance().dropDB()
    }
    finally {
        CacheFactory.getInstance().clearAll()
        localStorage.clear()
        sessionStorage.clear()
        window.location.href = '/login'
        window.location.reload()
    }
}
